import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, resolveAssetUrl } from "@/lib/api";
import { Plus, Pencil, Trash2, MoveUp, MoveDown } from "lucide-react";

export default function AdminPortfolioList() {
    const [projects, setProjects] = useState(null);
    const [busy, setBusy] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/portfolio");
            setProjects(data);
        } catch (err) {
            toast.error(formatApiError(err, "Impossibile caricare i progetti"));
            setProjects([]);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const handleDelete = async (slug, title) => {
        if (!window.confirm(`Eliminare definitivamente "${title}"?`)) return;
        setBusy(slug);
        try {
            await api.delete(`/admin/portfolio/${slug}`);
            toast.success("Progetto eliminato");
            setProjects((curr) => curr.filter((p) => p.slug !== slug));
        } catch (err) {
            toast.error(formatApiError(err, "Eliminazione fallita"));
        } finally {
            setBusy(null);
        }
    };

    const handleReorder = async (project, direction) => {
        if (!projects) return;
        const idx = projects.findIndex((p) => p.slug === project.slug);
        const swapIdx = direction === "up" ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= projects.length) return;
        const other = projects[swapIdx];
        setBusy(project.slug);
        try {
            await api.put(`/admin/portfolio/${project.slug}`, {
                ...project,
                order: other.order,
            });
            await api.put(`/admin/portfolio/${other.slug}`, {
                ...other,
                order: project.order,
            });
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Riordinamento fallito"));
        } finally {
            setBusy(null);
        }
    };

    return (
        <div data-testid="admin-portfolio-list" className="p-10 lg:p-14">
            <header className="flex flex-wrap items-end justify-between gap-6 mb-12">
                <div>
                    <p className="text-xs uppercase tracking-widest text-brass">
                        Portfolio
                    </p>
                    <h1 className="font-serif text-4xl text-cream font-light mt-3">
                        {projects?.length ?? "—"} progetti pubblicati
                    </h1>
                </div>
                <Link
                    to="/admin/portfolio/new"
                    data-testid="admin-portfolio-new"
                    className="group inline-flex items-center gap-3 bg-brass hover:bg-brass-light text-ink-900 px-6 py-3 text-xs tracking-widest uppercase transition-colors duration-500"
                >
                    <Plus size={16} /> Nuovo progetto
                </Link>
            </header>

            {projects === null ? (
                <p className="text-cream/40">Caricamento…</p>
            ) : projects.length === 0 ? (
                <div className="border border-dashed border-white/15 p-12 text-center text-cream/60">
                    Nessun progetto. Inizia creandone uno nuovo.
                </div>
            ) : (
                <ul className="divide-y divide-white/5 border border-white/5">
                    {projects.map((p, idx) => (
                        <li
                            key={p.slug}
                            data-testid={`admin-project-${p.slug}`}
                            className="flex items-center gap-6 p-5 hover:bg-ink-800/60 transition-colors"
                        >
                            <div className="w-24 h-24 shrink-0 bg-ink-700 overflow-hidden">
                                {p.cover && (
                                    <img
                                        src={resolveAssetUrl(p.cover)}
                                        alt={p.title}
                                        className="w-full h-full object-cover"
                                    />
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-[10px] uppercase tracking-widest text-brass">
                                    {p.category}
                                </p>
                                <h3 className="font-serif text-xl text-cream mt-1 truncate">
                                    {p.title}
                                </h3>
                                <p className="text-xs text-cream/50 mt-1 truncate">
                                    {p.location} · {p.year} ·{" "}
                                    <span className="font-mono">/{p.slug}</span>
                                </p>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    onClick={() => handleReorder(p, "up")}
                                    disabled={busy === p.slug || idx === 0}
                                    className="p-2 text-cream/50 hover:text-brass disabled:opacity-30"
                                    title="Sposta su"
                                    data-testid={`admin-project-up-${p.slug}`}
                                >
                                    <MoveUp size={16} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => handleReorder(p, "down")}
                                    disabled={
                                        busy === p.slug ||
                                        idx === projects.length - 1
                                    }
                                    className="p-2 text-cream/50 hover:text-brass disabled:opacity-30"
                                    title="Sposta giù"
                                    data-testid={`admin-project-down-${p.slug}`}
                                >
                                    <MoveDown size={16} />
                                </button>
                                <Link
                                    to={`/admin/portfolio/${p.slug}`}
                                    data-testid={`admin-project-edit-${p.slug}`}
                                    className="p-2 text-cream/70 hover:text-brass"
                                    title="Modifica"
                                >
                                    <Pencil size={16} />
                                </Link>
                                <button
                                    type="button"
                                    onClick={() => handleDelete(p.slug, p.title)}
                                    disabled={busy === p.slug}
                                    data-testid={`admin-project-delete-${p.slug}`}
                                    className="p-2 text-cream/70 hover:text-red-400 disabled:opacity-30"
                                    title="Elimina"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
